/**
 * Authentication API
 * Handles Clerk authentication and token management
 * Endpoints: /api/auth/*
 */
import apiClient, { createAuthConfig, isMockApi } from "../client";
import { mockVerifyToken, mockSyncUser, mockLogout } from "../mocks";

/**
 * Verify token with backend
 * @param {string} token - Clerk session token
 * @returns {Promise<Object>} - User info from backend
 */
export const verifyToken = async (token) => {
  if (isMockApi) return mockVerifyToken();
  const response = await apiClient.get('/api/auth/verify', createAuthConfig(token));
  return response.data;
};

// Dedupe: one sync per token so AuthSyncHandler + Sidebar (and Strict Mode) don't each fire a request.
// Keep a short successful TTL so withAuthReadyRequest + page mounts don't re-POST /auth/sync on every call.
let cachedSyncToken = null;
let cachedSyncPromise = null;
let cachedSyncResolvedAt = 0;
const SYNC_SUCCESS_TTL_MS = 30000;

/**
 * Sync user data from Clerk to backend database.
 * Multiple callers with the same token share one request; successful sync is
 * reused for SYNC_SUCCESS_TTL_MS (cleared on token change or failed sync).
 * @param {string} token - Clerk session token
 * @returns {Promise<Object>} - User data from backend
 */
export const syncUser = async (token) => {
  if (isMockApi) return mockSyncUser();
  if (cachedSyncToken !== token) {
    cachedSyncToken = null;
    cachedSyncPromise = null;
    cachedSyncResolvedAt = 0;
  }
  if (
    cachedSyncToken === token &&
    cachedSyncPromise &&
    (cachedSyncResolvedAt === 0 ||
      Date.now() - cachedSyncResolvedAt < SYNC_SUCCESS_TTL_MS)
  ) {
    return cachedSyncPromise;
  }
  cachedSyncToken = token;
  cachedSyncResolvedAt = 0;

  const clearSyncCacheIfCurrent = () => {
    if (cachedSyncToken === token) {
      cachedSyncToken = null;
      cachedSyncPromise = null;
      cachedSyncResolvedAt = 0;
    }
  };

  let referralPayload = {};
  try {
    const { getStoredReferralCode, clearStoredReferralCode } = await import(
      "./referral"
    );
    const code = getStoredReferralCode();
    if (code) {
      referralPayload = { referral_code: code };
    }
    cachedSyncPromise = apiClient
      .post("/api/auth/sync", referralPayload, createAuthConfig(token))
      .then((response) => {
        if (code) clearStoredReferralCode();
        cachedSyncResolvedAt = Date.now();
        return response.data;
      })
      .catch((err) => {
        // Don't permanently poison the cache — retries (Sidebar) must be able to sync again.
        clearSyncCacheIfCurrent();
        throw err;
      });
  } catch {
    cachedSyncPromise = apiClient
      .post("/api/auth/sync", {}, createAuthConfig(token))
      .then((response) => {
        cachedSyncResolvedAt = Date.now();
        return response.data;
      })
      .catch((err) => {
        clearSyncCacheIfCurrent();
        throw err;
      });
  }
  return cachedSyncPromise;
};

/**
 * Logout user
 * @param {string} token - Clerk session token
 * @returns {Promise<Object>}
 */
export const logout = async (token) => {
  if (isMockApi) return mockLogout();
  const response = await apiClient.post('/api/auth/logout', {}, createAuthConfig(token));
  return response.data;
};

export default {
  verifyToken,
  syncUser,
  logout,
};





